import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { faHourglass, faMinus, faPlus, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { IAirportSelectOutput } from "libraries/component/airport-select/airport-select.component";
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormGroupDirective, Validators } from "@angular/forms";
import { Yii } from "libraries/interfaces/Yii";
import { wenji } from "libraries/interfaces/IWenji";
import {
    AirSearchHeaderActionService
} from "../../air-search-header/air-search-header-action/air-search-header-action.service";
import { Subscription } from "rxjs";
import { IValidator } from "libraries/module/formbuilder/forms/utils/validators/IValidator";
import {
    cabinClassesList,
    IDropOption,
    maxViasList,
    viaDurationUnitList
} from 'libraries/module/formbuilder/forms/searchAirTrip/searchAirTrip.component';
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { EAirSearchFields } from "app_locobook/air-search/e-air-search-fields";
import { EAirSearchPermissions } from "app_locobook/air-search/e-air-search-permissions";
import { EAirSearchContext } from 'app_locobook/air-search/air-search.component';
import { IAirSearchSegment } from '../air-search-segment-oneway-return/air-search-segment-oneway-return.component';
import _cloneDeep from "lodash-es/cloneDeep";

export interface IDuration {
    max: number;
    unit: string;
}

export interface ITime {
    time?: Date;
}

export interface IBetween {
    min?: ITime;
    max: ITime;
}

export interface IBegin {
    value: Date;
    between?: IBetween;
}

export interface IEnd {
    xMoment: string;
    between?: IBetween;
}

export enum EWayType {
    Oneway = "Oneway",
    Return = "Return",
    Multiple = "Multiple"
}

export interface ICheckboxFieldSpecs extends IFieldSpecs {
    checkboxLabel?: string;
    disableRipple?: boolean;
}

export interface ISectionsVisibility {
    showCabine: boolean;
    showVia: boolean;
    showTimeSlots: boolean;
}

class Wording {
    public readonly multiple: string = Yii.t('wui-shared-js', 'Itinéraire');
    public readonly onlyAirport: string = Yii.t('wui-iqba-js', 'Cet aéroport uniquement');
    public readonly date: string = Yii.t('wui-shared-js', 'Date');
    public readonly invertFromTo: string = Yii.t('wui-shared-js', 'Inverser les lieux de départ et de destination');
    public readonly maxVias: string = Yii.t('wui-iqba-js', "Nombre d'escales");
    public readonly cabin: string = Yii.t('wui-shared-js', 'Classe cabine');
    public readonly timeSlots: string = Yii.t('wui-shared-js', 'Plage horaire');
    public readonly viaCabinPlaceholder: string = Yii.t('wui-shared-js', 'Aucune préférence');
    public readonly viaDurationUnitPlaceholder: string = Yii.t('wui-shared-js', 'Unité');
    public readonly nightFlight: string = Yii.t('wui-shared-js', "Vol de nuit");
    public readonly maxBeginTime: string = Yii.t('wui-shared-js', "Heure max de départ");
    public readonly minBedinTime: string = Yii.t('wui-shared-js', "Heure min de départ");
    public readonly minEndTime: string = Yii.t('wui-shared-js', "Heure min d'arrivée");
    public readonly maxDuration: string = Yii.t('wui-shared-js', 'Durée maximale');
}

@Component({
    selector: 'air-search-segment-multiple',
    templateUrl: './air-search-segment-multiple.component.html',
    styleUrls: ['./air-search-segment-multiple.component.scss']
})
export class AirSearchSegmentMultipleComponent implements OnInit, OnDestroy {
    @Input()
    public segmentValues: IAirSearchSegment[];
    @Input()
    public segmentLength: number;
    @Input()
    public airSearchContext: EAirSearchContext;
    @Output()
    public readonly emitSegmentValues: EventEmitter<IAirSearchSegment[]> = new EventEmitter<IAirSearchSegment[]>();

    public readonly iconPlus: IconDefinition = faPlus;
    public readonly iconMinus: IconDefinition = faMinus;
    public readonly iconHourglass: IconDefinition = faHourglass;
    public readonly today: Date = new Date();
    public onlyOneMonth: boolean;
    public airSearchForm: FormGroup;
    public wording: Wording = new Wording();
    public firstLocationCode: string;
    public airSegmentSearchForm: FormArray;
    public eAirSearchPermissions: typeof EAirSearchPermissions = EAirSearchPermissions;
    public sectionVisibility: ISectionsVisibility;
    public showNightFlight: boolean;
    public checkedNightFlight: boolean[] = [];
    public dateValidator: IValidator[] = [
        {
            validator: Validators.required,
            errors: {
                required: {
                    when: 'touched',
                    message: Yii.t('wui-shared-js', 'Date requise'),
                }
            }
        }
    ];
    public maxViasItems: IDropOption[] = maxViasList;
    public cabinItems: IDropOption[] = cabinClassesList;
    public viaDurationUnitItems: IDropOption[] = viaDurationUnitList;
    public eSegmentFields: typeof EAirSearchFields = EAirSearchFields;
    public airSearchHeaderActionSubscription: Subscription = new Subscription();

    constructor(
        private formGroupDirective: FormGroupDirective,
        private fb: FormBuilder,
        private airSearchHeaderActionService: AirSearchHeaderActionService,
    ) {
    }

    public ngOnInit(): void {
        this.initSectionVisibility();
        this.initSegmentForm();
        this.initAirSearchHeaderActionSubscription();
        this.showNightFlight = Boolean(wenji?.locobook?.search?.xMoment);
        this.airSegmentSearchForm.valueChanges.subscribe((value: any[]): void => {
            if (!this.sectionVisibility?.showTimeSlots) {
                this.sectionVisibility.showTimeSlots = (value || []).find((item: any): boolean => {
                    return item?.begin?.between?.min?.time ||
                        item?.begin?.between?.max?.time ||
                        item?.end?.between?.max?.time ||
                        item?.end?.xMoment;
                });
            }
            if (!this.sectionVisibility?.showVia) {
                this.sectionVisibility.showVia = (value || []).find((item: any): boolean => {
                    return item?.maxVias || item?.duration?.unit || item?.duration?.max;
                });
            }
            if (!this.sectionVisibility?.showCabine) {
                this.sectionVisibility.showCabine = (value || []).find((item: any): boolean => {
                    return item?.cabin;
                });
            }
        });
    }

    public ngOnDestroy(): void {
        this.emitSegmentValues.emit(this.airSegmentSearchForm.value);
        this.airSearchForm.removeControl(EAirSearchFields.segment);
        this.airSearchHeaderActionSubscription.unsubscribe();
    }


    public toggleSectionVisibility(section: string): void {
        this.sectionVisibility[section] = !this.sectionVisibility[section];
    }

    public toggleNightFlight(index: number): void {
        this.checkedNightFlight[index] = !this.checkedNightFlight[index];
        if (this.checkedNightFlight[index]) {
            this.airSegmentSearchForm.get([index, EAirSearchFields.end, EAirSearchFields.xMoment]).patchValue('NextDay');
        } else {
            this.airSegmentSearchForm.get([index, EAirSearchFields.end, EAirSearchFields.xMoment]).patchValue(null);
        }
    }

    public removeField(index: number): void {
        this.airSegmentSearchForm.removeAt(index);
        this.prefillFromToFields('remove');
    }

    public patchSegment(location: IAirportSelectOutput, field: string, index: number): void {
        if (location) {
            this.airSegmentSearchForm.get([index, field]).patchValue(location.code);
            this.airSegmentSearchForm.get([index, field === EAirSearchFields.from ? EAirSearchFields.isFromAirport : EAirSearchFields.isToAirport]).patchValue(location.isAirportOnly);
        } else {
            /*TODO : gérer le cas où si on saisi une valeur au From et on la efface par la suite, bien maj le formulaire envoie du vide et pas l'ancienne valeur*/
        }
    }

    public onSelectCabin(cabin: string, index: number): void {
        if (cabin && index === 0) {
            for (let i: number = 1; i < this.airSegmentSearchForm.length; i++) {
                if (!this.airSegmentSearchForm.get([i, EAirSearchFields.cabin]).value) {
                    this.airSegmentSearchForm.get([i, EAirSearchFields.cabin]).patchValue(cabin);
                }
            }
        }
    }

    /*public getMinBeginHour(i: number): Date {
        //console.log("min", this.airSegmentSearchForm.get([i, EAirSearchFields.begin, EAirSearchFields.between, EAirSearchFields.min]).value);
        return this.airSegmentSearchForm.get([i, EAirSearchFields.begin, EAirSearchFields.between, EAirSearchFields.min, EAirSearchFields.time]).value;
    }

    public getMaxBeginHour(i: number): Date {
       // console.log("max", this.airSegmentSearchForm.get([i, EAirSearchFields.begin, EAirSearchFields.between, EAirSearchFields.max]).value);
        return this.airSegmentSearchForm.get([i, EAirSearchFields.begin, EAirSearchFields.between, EAirSearchFields.max, EAirSearchFields.time]).value;
    }*/

    public getLastDate(_index: number): Date {
        if (_index === 0) {
            return this.today;
        }
        for (let i: number = _index; i > 0; i--) {
            const dateField: AbstractControl = this.airSegmentSearchForm.get([i - 1, EAirSearchFields.begin, EAirSearchFields.value]);
            if (dateField.value) {
                return dateField.value;
            }
        }
    }

    public prefillLastTo(): void {
        const getLastTo: AbstractControl = this.airSegmentSearchForm.get([this.airSegmentSearchForm.length - 1, EAirSearchFields.to]);
        const getLastIsToAirport: AbstractControl = this.airSegmentSearchForm.get([this.airSegmentSearchForm.length - 1, EAirSearchFields.isToAirport]);
        if (!getLastTo.value) {
            getLastTo.patchValue(this.airSegmentSearchForm.get([0, EAirSearchFields.from])?.value ?? null);
            getLastIsToAirport.patchValue(this.airSegmentSearchForm.get([0, EAirSearchFields.isFromAirport])?.value ?? null);
        }
    }

    public prefillFromToFields(operation: string): void {
        const getFirstTo: AbstractControl = this.airSegmentSearchForm.get([0, EAirSearchFields.to])?.value ?? null;
        const getFirstIsToAirport: AbstractControl = this.airSegmentSearchForm.get([0, EAirSearchFields.isToAirport])?.value ?? null;
        const getFirstFrom: AbstractControl = this.airSegmentSearchForm.get([0, EAirSearchFields.from])?.value ?? null;
        const getFirstIsFromAirport: AbstractControl = this.airSegmentSearchForm.get([0, EAirSearchFields.isFromAirport])?.value ?? null;
        // Return case : reverse from to
        if (this.airSegmentSearchForm.length === 2) {
            if (operation === 'add') {
                const lastToValue: string = this.airSegmentSearchForm.get([this.airSegmentSearchForm.length - 1, EAirSearchFields.to]).value;
                const lastFromValue: string = this.airSegmentSearchForm.get([this.airSegmentSearchForm.length - 1, EAirSearchFields.from]).value;
                if (!lastToValue) {
                    this.airSegmentSearchForm
                        .get([1, EAirSearchFields.to])
                        .patchValue(getFirstFrom);
                    this.airSegmentSearchForm
                        .get([1, EAirSearchFields.isToAirport])
                        .patchValue(getFirstIsFromAirport);
                }
                if (!lastFromValue) {
                    this.airSegmentSearchForm
                        .get([1, EAirSearchFields.from])
                        .patchValue(getFirstTo);
                    this.airSegmentSearchForm
                        .get([1, EAirSearchFields.isFromAirport])
                        .patchValue(getFirstIsToAirport);
                }
            } else {
                this.prefillLastTo();
            }
        } else {
            // Multiple Case : to = from(0) & from = to(i-1)
            if (this.airSegmentSearchForm.length > 2) {
                for (let i: number = 1; i < this.airSegmentSearchForm.length; i++) {
                    //patch TO
                    if (this.airSegmentSearchForm.get([i, EAirSearchFields.to]).value !== getFirstFrom) {
                        this.airSegmentSearchForm
                            .get([i, EAirSearchFields.to])
                            .patchValue(this.airSegmentSearchForm.get([i, EAirSearchFields.to]).value);
                        this.airSegmentSearchForm
                            .get([i, EAirSearchFields.isToAirport]).patchValue(this.airSegmentSearchForm.get([i, EAirSearchFields.isToAirport]).value);
                    } else {
                        this.airSegmentSearchForm
                            .get([i, EAirSearchFields.to])
                            .patchValue('');
                        this.airSegmentSearchForm.get([i, EAirSearchFields.isToAirport]).patchValue('');
                    }
                    //patch FROM
                    this.airSegmentSearchForm
                        .get([i, EAirSearchFields.from])
                        .patchValue(this.airSegmentSearchForm.get([i - 1, EAirSearchFields.to])?.value ?? null);
                    this.airSegmentSearchForm
                        .get([i, EAirSearchFields.isFromAirport])
                        .patchValue(this.airSegmentSearchForm.get([i - 1, EAirSearchFields.isToAirport])?.value ?? null);
                }
                this.prefillLastTo();
            }
        }
    }

    public addSegmentGroup(): void {
        const cabinFirstSegment: string = this.airSegmentSearchForm.get([this.airSegmentSearchForm.length - 1, EAirSearchFields.cabin])?.value ?? null;
        this.airSegmentSearchForm.push(this.fb.group({
            from: [null, Validators.required],
            isFromAirport: [null],
            to: [null, Validators.required],
            isToAirport: [null],
            begin: this.fb.group({
                value: [null, Validators.required],
                between: this.fb.group(
                    {
                        min: this.fb.group(
                            {
                                time: [null]
                            }),
                        max: this.fb.group(
                            {
                                time: [null]
                            })
                    }
                )
            }),
            maxVias: [null],
            cabin: [cabinFirstSegment],
            duration: this.fb.group({
                unit: [null],
                max: [null, [Validators.min(0)]]
            }),
            end: this.fb.group({
                xMoment: [null],
                between: this.fb.group(
                    {
                        max: this.fb.group(
                            {
                                time: [null]
                            })
                    }
                )
            })
        }));
        this.prefillFromToFields('add');
    }

    private initSegmentForm(): void {
        this.airSegmentSearchForm = this.fb.array([]);
        for (let i = 0; i < this.segmentLength; i++) {
            this.addSegmentGroup();
        }
        if (this.segmentValues) {
            this.segmentValues = this.segmentValues?.map((segment: IAirSearchSegment) => {
                return {
                    ...segment,
                    begin: {
                        value: segment.begin as Date
                    }
                };
            });
            this.airSegmentSearchForm.patchValue(this.segmentValues ?? null);
        }
        this.airSearchForm = this.formGroupDirective.form;
        this.airSearchForm.addControl(EAirSearchFields.segment, this.airSegmentSearchForm);
    }

    private initSectionVisibility(): void {
        const isContextPilotage: boolean = this.airSearchContext === EAirSearchContext.pilotage;
        this.sectionVisibility = {
            showCabine: isContextPilotage,
            showVia: isContextPilotage,
            showTimeSlots: isContextPilotage
        };
    }

    private initAirSearchHeaderActionSubscription(): void {
        this.airSearchHeaderActionSubscription.add(
            this.airSearchHeaderActionService.resetAirSearchFormObservable.subscribe((): void => {
                this.firstLocationCode = '';
            }));
    }

}
